'use client';

import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PaginationRootContext } from './context/pagination-root.context';
import type { PaginationPreviousButtonPropsInterface } from './types/pagination-previous-button.types';

/**
 * The previous page jump `button` element
 * @since 10.0.0
 */
export const PaginationPreviousButton = forwardRef<
  HTMLButtonElement,
  PaginationPreviousButtonPropsInterface
>((props: PaginationPreviousButtonPropsInterface, forwardedRef) => {
  const {
    children = (
      <Icon>
        <IconChevronLeftFilled />
      </Icon>
    ),
    onClick,
    ...rest
  } = props;
  const { setPageIndex, currentPageIndex = 0 } = useContext(PaginationRootContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setPageIndex?.(Math.max(currentPageIndex - 1, 0));
      onClick?.(event);
    },
    [setPageIndex, currentPageIndex, onClick],
  );

  return (
    <IconButton
      data-c11n-component="Pagination.PreviousButton"
      title="Previous page"
      tone="neutral"
      shape="circle"
      emphasis="bare"
      size="sm"
      onClick={handleClick}
      disabled={currentPageIndex <= 0}
      ref={forwardedRef}
      rel="prev"
      {...rest}
    >
      {children}
    </IconButton>
  );
});

PaginationPreviousButton.displayName = 'Pagination.PreviousButton';
